import classNames from 'classnames';

import { IPsySetting } from '../../../store/psyTools.slice';
import { useAppSelector } from '../../../hooks/redux';
import { declOfNum } from '../../../utils/decOfNum';
import { Anger } from '../../icons/reactions/anger';
import { Fear } from '../../icons/reactions/fear';
import { Joy } from '../../icons/reactions/joy';
import { Love } from '../../icons/reactions/love';
import { Sadness } from '../../icons/reactions/sadness';

import { Pressable } from '../../../shared/ui';

import { EmoList } from './emo-list';

export interface IEmoView {
  isPsiholog?: boolean;
  /** Карточный UI (слева смайлик+категория, справа теги) только для групповой терапии. */
  isGroup?: boolean;
  settings?: IPsySetting[];
  selectedEmo?: string[];
  onEmotionClick?: (item: string, selected: string[]) => void;
  reactionId: string[];
  setReactionId: React.Dispatch<React.SetStateAction<string[]>>;
  /** Мобильный 1:1: компактные эмоции-«таблетки», счётчик выбранных у категории, без большого отступа снизу */
  mobile?: boolean;
}

export const EmoView = ({
  isPsiholog = false,
  isGroup = false,
  settings,
  selectedEmo,
  onEmotionClick,
  reactionId,
  setReactionId,
  mobile = false,
}: IEmoView) => {
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);

  const selectEmo = (a: string) => {
    if (reactionId.includes(a)) {
      setReactionId((prev) => prev.filter((el) => el !== a));

      return;
    }
    setReactionId((prev) => [...prev, a]);
  };

  const icoStyle = 'w-[50px] h-[50px] min-w-[50px] min-h-[50px]';
  const emotionNames = ['Любовь', 'Радость', 'Грусть', 'Страх', 'Гнев'];
  const emotionIcons = [Love, Joy, Sadness, Fear, Anger];

  const countEmo = (emo: string) => {
    const getLengthOfEmo = (a: string) => {
      const s = psyTools.find((el) => el.name === 'emotions_detection')?.psy_tool_settings;
      const arr = s?.find((el) => el.name === a)?.value;
      return arr?.length || 0;
    };
    return getLengthOfEmo(emo);
  };

  /** Карточный UI только в групповой терапии у клиента; в парной — старый вид (категории раскрываются по клику). */
  const isCardLayout = Boolean(isGroup && !isPsiholog && onEmotionClick && settings?.length);

  if (isCardLayout) {
    return (
      <div className="flex flex-col gap-4">
        {emotionNames.map((emo, index) => {
          const count = countEmo(emo);
          const items = settings?.find((el) => el.name === emo)?.value ?? [];
          const EmotionIcon = emotionIcons[index];

          return (
            <div
              key={emo}
              className={classNames(
                'flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 rounded-control bg-white shadow-raised border border-line min-w-0',
                { 'pb-[10rem]': index + 1 === emotionNames.length },
              )}
            >
              <div className="flex items-center gap-3 shrink-0">
                <div className="w-[80px] h-[80px] min-w-[80px] min-h-[80px] rounded-control bg-surface-muted flex items-center justify-center">
                  <EmotionIcon className={icoStyle} stroke="#63656A" />
                </div>
                <div className="flex flex-col">
                  <p className="text-ink text-heading-md">{emo}</p>
                  <p className="text-ink-muted text-ui">
                    {count} {declOfNum(count, ['эмоция', 'эмоции', 'эмоций'])}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center min-w-0 flex-1">
                {items.map((item) => {
                  const selected = selectedEmo?.includes(item);
                  return (
                    <Pressable
                      key={item}
                      className={classNames(
                        'px-3 py-1.5 rounded-chip text-ui-strong transition-colors border hover:border-accent',
                        selected
                          ? 'bg-accent text-ink border-accent'
                          : 'bg-surface-muted text-ink border-line hover:bg-line',
                      )}
                      aria-pressed={selected}
                      onClick={() => onEmotionClick?.(item, selectedEmo ?? [])}
                    >
                      {item}
                    </Pressable>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={'flex flex-col xl:gap-8 gap-4 hover:cursor-pointer'}>
      {emotionNames.map((emo, index) => {
        const EmotionIcon = emotionIcons[index];
        const categoryItems = settings?.find((el) => el.name === emo)?.value ?? [];
        const selectedInCategory = selectedEmo ? categoryItems.filter((item) => selectedEmo.includes(item)).length : 0;

        return (
          <div
            key={emo}
            className={classNames('flex flex-col gap-4', {
              'pb-[10rem]': !mobile && reactionId.length > 0 && index + 1 === emotionNames.length,
            })}
          >
            <div className={'flex items-center gap-[15px] hover:cursor-pointer'} onClick={() => selectEmo(emo)}>
              <EmotionIcon className={icoStyle} stroke="#fff" />
              <span className="text-white text-ui">
                {emo}
                {mobile && selectedInCategory > 0 && (
                  <span className="ml-2 rounded-full bg-accent px-2 py-0.5 text-ui-xs font-medium text-ink">
                    {selectedInCategory}
                  </span>
                )}
                {isPsiholog && (
                  <>
                    <br />
                    {`(${countEmo(emo)} ${declOfNum(countEmo(emo), ['эмоция', 'эмоции', 'эмоций'])})`}
                  </>
                )}
              </span>
            </div>
            {reactionId.includes(emo) && (
              <EmoList
                items={categoryItems}
                selectedEmo={selectedEmo}
                onEmotionClick={onEmotionClick}
                mobile={mobile}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
